// Determine API URL based on environment
// Use Next.js API proxy for same-origin requests (fixes cookie issues)
const getApiUrl = (): string => {
  // In browser, use relative URL to go through Next.js API proxy
  if (typeof window !== 'undefined') {
    return ''; // Empty string means same origin - will use Next.js API proxy
  }
  
  // On server side, use direct backend URL
  if (process.env.NEXT_PUBLIC_API_URL) {
    return process.env.NEXT_PUBLIC_API_URL;
  }
  
  return 'http://localhost:3006';
};

const API_URL = getApiUrl();

export interface ReportMetadata {
  id: string;
  name: string;
  description: string;
  supportsDateRange: boolean;
  supportsCustomerId: boolean;
  supportsAgentId: boolean;
  supportsFileNumber?: boolean;
  supportsShipmentType?: boolean;
  supportsMaxMarginPct?: boolean;
  supportsOrderByProfit?: boolean;
  defaultLimit: number;
}

export interface RunReportParams {
  start?: string;
  end?: string;
  limit?: number;
  customer_id?: number;
  agent_id?: number;
  file_number?: string;
  shipment_type?: 'LCL' | 'FCL';
  max_margin_pct?: number;
  order_by_profit?: boolean;
}

export interface RunReportResponse {
  success: true;
  report_id: string;
  data: unknown[];
  row_count: number;
  query: string;
}

export interface AskResponse {
  success: true;
  question: string;
  report_id: string;
  data: unknown[];
  row_count: number;
  summary: string;
  query: string;
  params: Record<string, unknown>;
}

export interface AskResponseNeedsDateRange {
  success: false;
  needs_date_range: true;
  report_id: string;
  message: string;
}

export interface AskResponseNeedsFileNumber {
  success: false;
  needs_file_number: true;
  report_id: string;
  message: string;
}

export async function getReports(): Promise<ReportMetadata[]> {
  const response = await fetch(`${API_URL}/api/reports`, {
    credentials: 'include', // Include cookies for session
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch reports: ${response.statusText}`);
  }
  const data = await response.json();
  return data.reports;
}

export async function runReport(
  reportId: string,
  params?: RunReportParams
): Promise<RunReportResponse> {
  const response = await fetch(`${API_URL}/api/reports/run`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for session
    body: JSON.stringify({
      report_id: reportId,
      params: params || {},
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || `Failed to run report: ${response.statusText}`);
  }

  return response.json();
}

export async function askQuestion(
  question: string
): Promise<AskResponse | AskResponseNeedsDateRange | AskResponseNeedsFileNumber> {
  const response = await fetch(`${API_URL}/api/ask`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for session
    body: JSON.stringify({ question }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || `Failed to process question: ${response.statusText}`);
  }

  return response.json();
}

